import { Flex, useDisclosure } from '@chakra-ui/react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router'
import useFetch, { CachePolicies } from 'use-http'
import { DetailPageSideMenuLayout } from '.'
import { BaseLayout } from '..'
import { FeaturesEnum } from '../../../utils/features'
import CustomPlanCTA from '../../components/CustomPlan'
import { ShareAssetModal } from '../../components/MediaLibrary/MediaOperations'
import AccessDeniedView from '../../components/MediaLibrary/View/AccessDeniedView'
import NotFoundView from '../../components/MediaLibrary/View/NotFoundView'
import type {
  ViewableParentRequest,
  ViewableParentSuccessResponse
} from '../../interfaces'
import type { FileDetailsResponse } from '../../interfaces/FileDetailsResponse'
import { FileDetailPage } from '../../pages/MediaLibrary'
import { UserContext } from '../../providers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'

const FileDetailBaseLayout = (): React.ReactElement => {
  const { isPublicLink, urlPrefix, clientInfo, assetType } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const clientNumber = user?.clientNumber ?? clientInfo?.clientNumber
  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()
  const {
    params: { mediaCollectionId, fileId = '', versionId },
  } = useRouteMatch<MLFileDetailRouteParams>()

  const url = useMemo<string>(() => {
    if (versionId) {
      return `${urlPrefix}api/v2/files/${fileId}/versions/${versionId}`
    }
    return `${urlPrefix}api/v2/files/${fileId}/details`
  }, [fileId, versionId, urlPrefix])

  const {
    data: fileDetails,
    error: fileDetailsError,
    get,
    loading,
  } = useFetch<FileDetailsResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const getFileDetails = useCallback(() => get(url), [url])
  const {
    post,
    loading: loadingViewableParent,
    data: viewableParent,
    error: viewableParentError,
  } = useFetch<ViewableParentSuccessResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getViewableParent = useCallback(() => {
    const requestBody: ViewableParentRequest = {
      assetId: fileId,
    }
    if (mediaCollectionId) {
      requestBody.mediaCollectionId = mediaCollectionId
    }

    return post(
      `${urlPrefix}api/clients/${clientNumber}/access-control/viewable-parent`,
      requestBody,
    )
  }, [fileId, mediaCollectionId, clientNumber, urlPrefix, assetType])
  useEffect(() => {
    if (
      (
        isPublicLink &&
        assetType !== "file"
      ) ||
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      void getViewableParent()
    }
  }, [getViewableParent, user, isPublicLink])
  useEffect(() => {
    if (
      isPublicLink ||
      (viewableParent && !viewableParentError) ||
      (
        user &&
        !user.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
      )
    ) {
      void getFileDetails()
    }
  }, [viewableParent, url, viewableParentError, user, isPublicLink])
  const { t } = useTranslation()
  const [isTagsModalOpen, toggleTagsModal] = useState(false)

  const unionisedError = viewableParentError ?? fileDetailsError

  return (
    isPublicLink ||
    user?.pricingPlan.featuresList.includes(FeaturesEnum.MEDIA_LIBRARY_STORAGE)
  ) ? (
    <React.Fragment>
      {
        unionisedError ? (
          unionisedError.name === "403" ? (
            <Flex
              height="100vh"
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <AccessDeniedView />
            </Flex>
          ) : (
            unionisedError.name === "404" ? (
              <Flex
                height="100vh"
                width="100%"
                alignItems="center"
                justifyContent="center"
              >
                <NotFoundView />
              </Flex>
            ) : null
          )
        ) : (
          <BaseLayout
            menu={
              <DetailPageSideMenuLayout
                fileDetails={fileDetails}
                isTagsModalOpen={isTagsModalOpen}
                toggleTagsModal={toggleTagsModal}
                getFileDetails={getFileDetails}
                loading={
                  loadingViewableParent
                    ? loading || loadingViewableParent
                    : loading
                }
              />
            }
          >
            <FileDetailPage
              fileDetails={fileDetails}
              toggleTagsModal={toggleTagsModal}
              getFileDetails={getFileDetails}
              onOpenShareModal={onOpenShareAssetModal}
              loading={
                loadingViewableParent ? loading || loadingViewableParent : loading
              }
              viewableParent={viewableParent}
            />
          </BaseLayout>
        )
      }
      {
        !isPublicLink ? (
          <ShareAssetModal
            isOpen={isOpenShareAssetModal}
            onClose={onCloseShareAssetModal}
            getViewableParent={getViewableParent}
            assetId={fileDetails?.fileId ?? ''}
            assetName={fileDetails?.name ?? ''}
            assetType={fileDetails?.type ?? ''}
          />
        ) :
        null
      }
    </React.Fragment>
  ) : (
    <BaseLayout>
      <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
    </BaseLayout>
  )
}

export default FileDetailBaseLayout

export interface MLFileDetailRouteParams {
  fileId: string
  versionId?: string
  mediaCollectionId?: string
}
